import React from "react";
import { CV } from "../components/cv";
import FelixImg from "@/assets/images/felix.png";

export default function CVFelixTran() {
  React.useEffect(() => {
    document.title = "BoldBrave CV - Felix Tran";
  }, []);

  return (
    <CV
      name="Felix Tran"
      photo={FelixImg}
      title="Senior Platform Engineer · DevOps · Cloud Infrastructure Architect"
      subtitle="Eight years of building cloud platforms for regulated environments, where security-by-default and data residency are requirements rather than choices."
      summary="Senior platform engineer specialised in Azure and GCP for industries where downtime is a compliance event. Builds sovereign cloud foundations that handle data residency and strict local regulation without slowing teams down, and leads migrations from legacy setups to cloud-native environments — typically reaching 99.99% uptime while cutting unnecessary spend by around 30%. A firm believer in Infrastructure as Code: if it has to be done twice, it gets automated, using Terraform and Kubernetes to let developers move fast without breaking the underlying security."
      skills={{
        "Platform & Orchestration": [
          "Kubernetes",
          "AKS",
          "Docker",
          "Platform Engineering",
          "Nginx ingress",
        ],
        "Infrastructure as Code": ["Terraform", "Ansible / AWX", "Bash", "Python"],
        Cloud: ["Azure", "GCP", "Sovereign cloud & data residency"],
        "CI/CD": [
          "GitHub Actions",
          "GitLab CI",
          "Azure DevOps Pipelines",
          "Jenkins",
          "JFrog Artifactory",
          "Sonatype Nexus",
        ],
        "Observability & Data": ["Splunk", "Grafana", "PostgreSQL", "MongoDB"],
        Development: ["Golang", "Python", "Shell"],
        Certifications: [
          "Professional Cloud Developer",
          "Google IT Automation with Python",
          "GitHub Foundations",
          "Jenkins Engineer",
          "Microsoft Certified: Azure Fundamentals",
        ],
        Languages: ["Swedish (native)", "English (full professional)"],
      }}
      experience={[
        {
          role: "CEO and Founder",
          company: "Cloud Ape",
          period: "2024 – Present",
          description:
            "Runs an independent consultancy focused on cloud platform engineering, DevOps and infrastructure architecture.",
        },
        {
          role: "Platform Engineer",
          company: "Inter IKEA Group",
          period: "2024 – 2026",
          description:
            "Managed GitHub Enterprise infrastructure with Azure cloud integration and API automation for a global organisation.",
          highlights: [
            "Built CI/CD pipelines using GitHub Actions and Docker containerisation.",
            "Implemented observability with Splunk and managed JFrog Artifactory for artifact management.",
            "Automated security, access control and repository management at scale.",
          ],
        },
        {
          role: "Industrial Automation & DevOps",
          company: "Pågen",
          period: "2025",
          description:
            "Delivered a secure on-prem solution for managing sensitive production data in the bakery industry.",
          highlights: [
            "Designed and implemented a POC platform for remote management and configuration of Raspberry Pi devices using Ansible AWX.",
            "Integrated Azure DevOps Pipelines with Sonatype Nexus for automated building and versioning of Docker images.",
            "Automated previously manual processes to reduce error margins and time spent on production updates.",
          ],
        },
        {
          role: "Site Reliability Engineer",
          company: "Accessy AB",
          period: "2023 – 2024",
          description:
            "Designed and operated Azure Kubernetes infrastructure with Terraform IaC.",
          highlights: [
            "Established GitLab CI/CD pipelines and Docker-based deployment automation.",
            "Implemented monitoring, PostgreSQL database management and Nginx ingress.",
            "Security hardening and high-availability platform operations.",
          ],
        },
        {
          role: "Cloud Developer",
          company: "Qlik",
          period: "2021 – 2023",
          description:
            "Built cloud-native microservices on Kubernetes using Golang and MongoDB.",
          highlights: [
            "Implemented API integrations, event-driven architectures and Grafana monitoring.",
            "Maintained Splunk observability and scalable container orchestration.",
          ],
        },
        {
          role: "Cloud Consultant",
          company: "Forefront Consulting",
          period: "2021",
          description:
            "Architected GCP solutions with Terraform IaC and Google Cloud Build CI/CD for enterprise clients.",
          highlights: [
            "Implemented Google Cloud AutoML and AI Platform integration.",
          ],
        },
        {
          role: "Cloud Consultant",
          company: "Softhouse Consulting",
          period: "2020",
          description:
            "Designed and maintained automated delivery pipelines using Jenkins and GitLab CI for client projects.",
          highlights: [
            "Provisioned and managed scalable cloud environments with Terraform.",
            "Managed build artifacts and dependencies in JFrog Artifactory.",
            "Developed Bash and Python scripts to automate operational tasks and improve developer self-service.",
          ],
        },
        {
          role: "Cloud Consultant",
          company: "Sogeti",
          period: "2019 – 2020",
          description:
            "Assisted enterprise clients in migrating legacy workloads to the cloud, focusing on containerisation and microservices architecture.",
          highlights: [
            "Configured Jenkins build nodes and optimised pipeline execution times.",
            "Standardised environments with Docker to ensure environment parity across teams.",
          ],
        },
        {
          role: "IT Support Specialist",
          company: "Tieto",
          period: "2018 – 2019",
          description:
            "High-level technical support for complex IT infrastructure with strict SLAs, plus server environment administration.",
          highlights: [
            "Automated routine system health checks and reporting with Bash scripting.",
          ],
        },
        {
          role: "CEO / Operations Manager / System Operator",
          company: "Easy Asia Trading AB",
          period: "2005 – 2017",
          description:
            "Progressed from system operator to operations manager and CEO, with full responsibility for operations and business.",
        },
      ]}
      education={[
        {
          degree: "Specialisation in Cloud Computing & Distributed Systems",
          institution: "Blekinge Tekniska Högskola",
          year: "2018 – 2020",
        },
        {
          degree: "Higher Vocational Education Diploma, IT Support Specialist",
          institution: "EC Utbildning",
          year: "2018 – 2019",
        },
        {
          degree: "High School Diploma, Technology Program",
          institution: "Polhem",
          year: "2001 – 2004",
        },
      ]}
    />
  );
}
